import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {OfferNode} from '../types'
import {ancestorKeys, isExpandedByDefault, nodeKey} from '../utils/expansion'
import {flattenTree} from '../utils/networkStats'
import {nodeAnchorId} from './KpiPanelContext'

type ExpansionContextValue = {
  /** Whether `node`'s children are currently shown. */
  isExpanded: (node: OfferNode) => boolean
  /** Flips `node` between expanded and collapsed. */
  toggle: (node: OfferNode) => void
  /** Expands every node in the tree. */
  expandAll: () => void
  /** Collapses every node, leaving only the root cards visible. */
  collapseAll: () => void
  /** Expands the branch down to the offer card of `offerId`, then scrolls it into view. */
  revealAndScroll: (offerId: string) => void
}

/**
 * Smoothly scrolls a node's offer card into view. A no-op when the card is not mounted (e.g. still
 * inside a collapsed branch), so navigation can never throw.
 */
const scrollNodeIntoView = (offerId: string): void => {
  document.getElementById(nodeAnchorId(offerId))?.scrollIntoView({behavior: 'smooth', block: 'center'})
}

/**
 * Fallback used when a node-navigating component (the Agent Chain breadcrumb, the contributions
 * table) is rendered outside a provider — e.g. in isolation in a unit test. Expansion is then a
 * read-only, level-derived default and there is no tree to open, so revealing a node is just a
 * direct scroll.
 */
const defaultValue: ExpansionContextValue = {
  isExpanded: (node) => isExpandedByDefault(node.level),
  toggle: () => {},
  expandAll: () => {},
  collapseAll: () => {},
  revealAndScroll: scrollNodeIntoView,
}

const ExpansionContext = createContext<ExpansionContextValue>(defaultValue)

type ExpansionProviderProps = {
  /** Root nodes of the rendered tree; the provider derives the full node set from them. */
  roots: OfferNode[]
  children: ReactNode
}

/**
 * Holds the tree's expand/collapse state in one place so a deep network stays fast and controllable
 * (PT-5721): nodes beyond level 2 start collapsed and their children only mount once expanded (lazy
 * mount), an "Expand all / Collapse all" control can flip the whole tree at once, and navigating to a
 * node buried in a collapsed branch opens the path to it first.
 *
 * State is a sparse map of explicit per-node choices; a node with no entry falls back to its
 * level-based default. Summary stats are computed elsewhere from the full tree, so they stay correct
 * regardless of what is expanded (stats are data-driven, not render-driven).
 *
 * @param roots - Root nodes produced by `buildOfferTree`.
 * @param children - The control bar and the rendered tree.
 */
export const ExpansionProvider = ({roots, children}: ExpansionProviderProps) => {
  const [overrides, setOverrides] = useState<Map<string, boolean>>(() => new Map())
  const [pendingScroll, setPendingScroll] = useState<string | null>(null)

  const {allKeys, nodesByKey} = useMemo(() => {
    const flat = flattenTree(roots)
    return {
      allKeys: flat.map(nodeKey),
      nodesByKey: new Map(flat.map((node) => [nodeKey(node), node])),
    }
  }, [roots])

  const expandedOf = useCallback(
    (node: OfferNode): boolean => overrides.get(nodeKey(node)) ?? isExpandedByDefault(node.level),
    [overrides],
  )

  const toggle = useCallback((node: OfferNode) => {
    setOverrides((prev) => {
      const key = nodeKey(node)
      const next = new Map(prev)
      next.set(key, !(prev.get(key) ?? isExpandedByDefault(node.level)))
      return next
    })
  }, [])

  // Expand-all/collapse-all overwrite every node explicitly (a full new map), so no node is left on
  // its level default — one synchronous update flips the whole tree.
  const setEvery = useCallback(
    (value: boolean) => setOverrides(() => new Map(allKeys.map((key) => [key, value]))),
    [allKeys],
  )
  const expandAll = useCallback(() => setEvery(true), [setEvery])
  const collapseAll = useCallback(() => setEvery(false), [setEvery])

  const revealAndScroll = useCallback(
    (offerId: string) => {
      const node = nodesByKey.get(offerId)
      const collapsedAncestors = node
        ? ancestorKeys(node).filter((key) => {
            const ancestor = nodesByKey.get(key)
            return ancestor ? !(overrides.get(key) ?? isExpandedByDefault(ancestor.level)) : false
          })
        : []

      // Common case (the target is already on screen, e.g. an upstream breadcrumb pill): scroll now.
      if (collapsedAncestors.length === 0) {
        scrollNodeIntoView(offerId)
        return
      }

      // Otherwise open the branch first and defer the scroll until the target has mounted.
      setOverrides((prev) => {
        const next = new Map(prev)
        collapsedAncestors.forEach((key) => next.set(key, true))
        return next
      })
      setPendingScroll(offerId)
    },
    [nodesByKey, overrides],
  )

  // Runs after the newly-expanded ancestors have committed to the DOM, so the target card exists.
  useEffect(() => {
    if (pendingScroll == null) return
    scrollNodeIntoView(pendingScroll)
    setPendingScroll(null)
  }, [pendingScroll])

  const value = useMemo<ExpansionContextValue>(
    () => ({isExpanded: expandedOf, toggle, expandAll, collapseAll, revealAndScroll}),
    [expandedOf, toggle, expandAll, collapseAll, revealAndScroll],
  )

  return <ExpansionContext.Provider value={value}>{children}</ExpansionContext.Provider>
}

/**
 * Reads the shared expansion state. Outside a provider it returns a safe, level-based default so a
 * node-navigating component can still be rendered in isolation.
 */
export const useExpansion = (): ExpansionContextValue => useContext(ExpansionContext)
